import apiClient, { handleApiError, handleApiResponse } from './api';
import type {
  Expense,
  ExpenseFilters,
  ExpenseListResponse,
  ExpenseMutationData,
  ExpenseResponse,
  TaxListResponse,
} from '../types/expense';

const BASE_PATH = '/agency/expenses';

const buildQuery = (filters: ExpenseFilters = {}) => {
  const params = new URLSearchParams();
  if (filters.page) params.append('page', String(filters.page));
  if (filters.limit) params.append('limit', String(filters.limit));
  if (filters.search) params.append('search', filters.search);
  if (filters.scope) params.append('scope', filters.scope);
  if (filters.category) params.append('category', filters.category);
  if (filters.status && filters.status !== 'all') params.append('status', filters.status);
  if (filters.origin) params.append('origin', filters.origin);
  if (filters.vehicleId) params.append('vehicleId', filters.vehicleId);
  if (filters.employeeId) params.append('employeeId', filters.employeeId);
  if (filters.bookingId) params.append('bookingId', filters.bookingId);
  if (filters.from) params.append('from', filters.from);
  if (filters.to) params.append('to', filters.to);
  return params;
};

const expenseService = {
  listExpenses: async (filters: ExpenseFilters = {}): Promise<ExpenseListResponse['data']> => {
    try {
      const query = buildQuery(filters);
      const response = await apiClient.get<ExpenseListResponse>(`${BASE_PATH}?${query.toString()}`);
      return handleApiResponse(response).data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  getExpenseById: async (expenseId: string): Promise<Expense> => {
    try {
      const response = await apiClient.get<ExpenseResponse>(`${BASE_PATH}/${expenseId}`);
      return handleApiResponse(response).data.expense;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  createExpense: async (data: ExpenseMutationData): Promise<Expense> => {
    try {
      const response = await apiClient.post<ExpenseResponse>(BASE_PATH, data);
      return handleApiResponse(response).data.expense;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  updateExpense: async (expenseId: string, data: Partial<ExpenseMutationData>): Promise<Expense> => {
    try {
      const response = await apiClient.put<ExpenseResponse>(`${BASE_PATH}/${expenseId}`, data);
      return handleApiResponse(response).data.expense;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  deleteExpense: async (expenseId: string): Promise<void> => {
    try {
      await apiClient.delete(`${BASE_PATH}/${expenseId}`);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  approveExpense: async (expenseId: string): Promise<Expense> => {
    try {
      const response = await apiClient.patch<ExpenseResponse>(`${BASE_PATH}/${expenseId}/approve`);
      const result = handleApiResponse(response).data;
      if (
        result.audit?.persisted !== true
        || result.expense.status !== 'approved'
        || !Number.isFinite(Number(result.expense.amount))
        || Number(result.expense.amount) <= 0
      ) {
        throw new Error('The server could not verify that this expense was approved.');
      }
      return result.expense;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Settle (mark paid) a vehicle recurring expense for its current cycle. Accepts
  // a recurring template id (from the expiry notification) or an occurrence id.
  settleExpense: async (expenseId: string): Promise<Expense> => {
    try {
      const response = await apiClient.patch<ExpenseResponse>(`${BASE_PATH}/${expenseId}/settle`);
      return handleApiResponse(response).data.expense;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  rejectExpense: async (expenseId: string, reason?: string): Promise<Expense> => {
    try {
      const response = await apiClient.patch<ExpenseResponse>(`${BASE_PATH}/${expenseId}/reject`, {
        reason,
        rejectionReason: reason,
      });
      return handleApiResponse(response).data.expense;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  importExpenses: async (file: File): Promise<{
    importedCount: number;
    skippedCount: number;
    skipped: Array<{ row: number; reason: string }>;
    expenses: Expense[];
  }> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post(`${BASE_PATH}/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return handleApiResponse(response).data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  generateRecurringExpenses: async (): Promise<{ created: number; templates: number; expenses: Expense[] }> => {
    try {
      const response = await apiClient.post(`${BASE_PATH}/generate-recurring`);
      return handleApiResponse(response).data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  listTaxes: async (filters: Pick<ExpenseFilters, 'page' | 'limit' | 'search' | 'vehicleId' | 'from' | 'to'> = {}): Promise<TaxListResponse['data']> => {
    try {
      const params = new URLSearchParams();
      if (filters.page) params.append('page', String(filters.page));
      if (filters.limit) params.append('limit', String(filters.limit));
      if (filters.search) params.append('search', filters.search);
      if (filters.vehicleId) params.append('vehicleId', filters.vehicleId);
      if (filters.from) params.append('from', filters.from);
      if (filters.to) params.append('to', filters.to);
      const response = await apiClient.get<TaxListResponse>(`${BASE_PATH}/taxes?${params.toString()}`);
      return handleApiResponse(response).data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },
};

export default expenseService;
